import type { ReactNode } from 'react';
import {
  ClerkProvider as ClerkProviderImpl,
  useAuth as useClerkAuth,
  useUser as useClerkUser,
  RedirectToSignIn,
} from '@clerk/clerk-react';
import { Box, CircularProgress } from '@mui/material';
import { MockAuthProvider } from './MockAuthProvider';
import { AuthContext, type AuthState } from './AuthContext';

function ClerkAuthBridge({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, getToken, signOut } = useClerkAuth();
  const { user } = useClerkUser();

  const value: AuthState = {
    isLoaded,
    isSignedIn: isSignedIn ?? false,
    userId: user?.id ?? null,
    email: user?.primaryEmailAddress?.emailAddress ?? null,
    getToken: async () => (await getToken()) ?? null,
    signOut: async () => {
      await signOut();
    },
  };

  if (!isLoaded) {
    return (
      <AuthContext.Provider value={value}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
          }}
        >
          <CircularProgress />
        </Box>
      </AuthContext.Provider>
    );
  }

  if (!isSignedIn) {
    return (
      <AuthContext.Provider value={value}>
        <RedirectToSignIn />
      </AuthContext.Provider>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AppAuthProvider({ children }: { children: ReactNode }) {
  const useMock = import.meta.env.VITE_USE_MOCK_AUTH === 'true';
  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

  if (useMock || !publishableKey) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return (
    <ClerkProviderImpl
      publishableKey={publishableKey}
      afterSignOutUrl="/"
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
    >
      <ClerkAuthBridge>{children}</ClerkAuthBridge>
    </ClerkProviderImpl>
  );
}
